import { useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import formatPeriod from '../../../core/commom/formatCoursePeriod';
import getCourseTags from '../../../core/commom/getCourseTags';
import Slider from '../../../core/components/atoms/Slider';
import CourseWeekCard from '../../../core/components/molecules/CourseWeekCard';
import { useMarkProgramAsSelected } from '../../../core/hooks/useMarkProgramAsSelected';
import { useScreenSize } from '../../../core/hooks/useScreenSize';
import { useWindowBreakpoints } from '../../../core/hooks/useWindowBreakpoints';
import { CourseProgramModal } from '../components/CourseProgramModal';
import RegularCoursesList from '../components/RegularCoursesList';
import CourseHeader from '../components/courseHeader';
import CourseInfo from '../components/courseInfo';
import { useMyRegularCourses } from '../hooks/useRegularCourse';
import { CourseDto } from '../models/Course.dto';
import { useCoursesStore } from '../stores/courseStore';

export default function RegularCourses() {
  const windowBreakpoints = useWindowBreakpoints();
  const screenSize = useScreenSize();

  const [courseProgramModalIsOpen, setCourseProgramModalIsOpen] =
    useState(false);
  const [selectedWeekId, setSelectedWeekId] = useState<string | null>(null);

  const {
    courseShow,
    userMatriculatedCourse,
    showAllCourses,
    setShowAllCourses,
    hideAllCourses,
    setShowMatriculatedCourse,
    showMatriculatedCourse,
  } = useCoursesStore();
  const { isLoading } = useMyRegularCourses();
  const selectProgram = useMarkProgramAsSelected();

  const userHasMatriculatedCourse = !!userMatriculatedCourse;

  function openCourseProgramModal() {
    setCourseProgramModalIsOpen(true);
  }

  async function openProgram(course: CourseDto, programId: string) {
    await selectProgram.mutateAsync({ courseId: course.id, programId });
    window.open(`/cursos/${course.slug}`, '_self');
  }

  const showWeeks = !!(
    courseShow &&
    courseShow.weeks &&
    courseShow.weeks.length > 0
  );
  const displayedWeeks = showWeeks
    ? courseShow.weeks.filter((it: any) => it.display)
    : [];
  const sortedWeeks = [...displayedWeeks].sort((a: any, b: any) =>
    courseShow?.slug === 'desafio-30-dias-revisao-2024'
      ? a.order - b.order
      : b.order - a.order
  );
  const sortedWeeksOnMobile = [...displayedWeeks].sort(
    (a: any, b: any) => b.order - a.order
  );
  const defaultSelectedWeekId =
    sortedWeeks.find((it: any) => it.isCurrentWeek)?._id ||
    sortedWeeks[0]?._id ||
    null;

  useEffect(() => {
    if (!sortedWeeks.length) {
      if (selectedWeekId !== null) {
        setSelectedWeekId(null);
      }
      return;
    }

    const hasSelectedWeek = sortedWeeks.some(
      (week: any) => week._id === selectedWeekId
    );

    if (!hasSelectedWeek) {
      setSelectedWeekId(defaultSelectedWeekId);
    }
  }, [defaultSelectedWeekId, selectedWeekId, sortedWeeks]);

  if (isLoading) {
    return (
      <div>
        <ContentLoader
          width={'100%'}
          height={980}
          viewBox='0 0 1440 980'
          backgroundColor='#f0f0f0'
          foregroundColor='#dedede'
          preserveAspectRatio='none'
        >
          <rect x='0' y='0' rx='0' ry='0' width='1440' height='300' />

          <rect x='230' y='145' rx='4' ry='4' width='150' height='12' />
          <rect x='230' y='168' rx='12' ry='12' width='82' height='22' />
          <rect x='230' y='205' rx='6' ry='6' width='250' height='42' />
          <rect x='230' y='267' rx='8' ry='8' width='165' height='40' />
          <rect x='410' y='267' rx='8' ry='8' width='145' height='40' />
          <rect x='570' y='276' rx='4' ry='4' width='100' height='20' />
          <rect x='1210' y='42' rx='4' ry='4' width='170' height='18' />

          <circle cx='285' cy='367' r='10' />
          <circle cx='1155' cy='367' r='10' />

          <rect x='310' y='332' rx='8' ry='8' width='92' height='112' />
          <rect x='418' y='332' rx='8' ry='8' width='92' height='112' />
          <rect x='526' y='332' rx='8' ry='8' width='92' height='112' />
          <rect x='634' y='332' rx='8' ry='8' width='92' height='112' />
          <rect x='742' y='332' rx='8' ry='8' width='92' height='112' />
          <rect x='850' y='332' rx='8' ry='8' width='92' height='112' />
          <rect x='958' y='332' rx='8' ry='8' width='92' height='112' />
          <rect x='1066' y='332' rx='8' ry='8' width='92' height='112' />

          <rect x='285' y='475' rx='10' ry='10' width='870' height='460' />
          <rect x='315' y='505' rx='4' ry='4' width='150' height='12' />
          <rect x='315' y='529' rx='6' ry='6' width='200' height='34' />

          <rect x='315' y='595' rx='4' ry='4' width='110' height='14' />
          <rect x='315' y='617' rx='4' ry='4' width='650' height='10' />

          <rect x='315' y='655' rx='4' ry='4' width='95' height='14' />
          <rect x='315' y='677' rx='4' ry='4' width='720' height='10' />

          <rect x='315' y='715' rx='4' ry='4' width='105' height='14' />
          <rect x='315' y='737' rx='4' ry='4' width='620' height='10' />

          <rect x='315' y='775' rx='4' ry='4' width='90' height='14' />
          <rect x='315' y='797' rx='4' ry='4' width='760' height='10' />

          <rect x='315' y='835' rx='4' ry='4' width='100' height='14' />
          <rect x='315' y='857' rx='4' ry='4' width='680' height='10' />
        </ContentLoader>
      </div>
    );
  }

  const weekWithContent =
    sortedWeeks.find((it: any) => it._id === selectedWeekId) ||
    sortedWeeks.find((it: any) => it.isCurrentWeek) ||
    sortedWeeks[0];
  const weekAreas = (weekWithContent?.areas || [])
    .map((areaItem: any) => {
      const title = areaItem?.area?.title || areaItem?.title;

      const contents = (
        areaItem?.contents ||
        areaItem?.subjects?.map((subject: any) => subject?.title) ||
        areaItem?.subareas?.map((subarea: any) => subarea?.title) ||
        []
      )
        .map((content: any) =>
          typeof content === 'string'
            ? content.trim()
            : content?.title?.trim() || ''
        )
        .filter(Boolean);

      return {
        id: areaItem?.area?.id || areaItem?._id,
        slug: areaItem?.area?.slug || areaItem?.slug,
        title,
        contents,
      };
    })
    .filter((area: any) => !!area.title);
  const showWeekContentSection = weekAreas.some(
    (area: any) => area.contents.length > 0
  );

  const moveHeaderOnMobile = showWeeks
    ? screenSize < windowBreakpoints.md
    : screenSize < windowBreakpoints.sm;

  return (
    <div>
      {courseShow && showMatriculatedCourse && (
        <div>
          <CourseProgramModal
            courseKind={courseShow.kind}
            courseTitle={courseShow.title}
            programsData={courseShow.weeks || []}
            isOpen={courseProgramModalIsOpen}
            onClose={() => {
              setCourseProgramModalIsOpen(false);
            }}
          />
          <CourseHeader
            backgroundImage={courseShow ? courseShow.thumbnail : ''}
            showButtonHeader={!showAllCourses}
            textButtonHeader='Ver outros cursos regulares'
            handleClickButtonHeader={() => {
              setShowAllCourses();
              setShowMatriculatedCourse(false);
            }}
            moveHeaderOnMobile={moveHeaderOnMobile}
          >
            <CourseInfo
              showDropButton={false}
              slug={courseShow.slug}
              courseStatus={
                courseShow && courseShow.userStartedAt
                  ? 'inprogress'
                  : 'notstarted'
              }
              tagsPosition={moveHeaderOnMobile ? 'bellowTitle' : 'aboveTitle'}
              courseType={courseShow ? courseShow.kind : 'regular'}
              tags={getCourseTags(courseShow)}
              title={courseShow ? courseShow.title : ''}
              openCourseProgramModal={openCourseProgramModal}
              moveHeaderOnMobile={moveHeaderOnMobile}
            />
          </CourseHeader>

          {showWeeks && screenSize >= windowBreakpoints.sm && (
            <div className='mx-auto max-w-screen-xl px-4 pt-8 md:px-20'>
              <Slider
                id='doing-course-my-area'
                slides={sortedWeeks.map((it: any, index: number) => {
                  return (
                    <CourseWeekCard
                      key={'doing-course-card-' + index}
                      id={it._id}
                      isCurrent={it.isCurrentWeek}
                      title={it.title}
                      date={formatPeriod(it.startAt, it.endAt)}
                      onCardClick={() => {
                        setSelectedWeekId(it._id);
                      }}
                      onClick={() => {
                        openProgram(courseShow, it._id);
                      }}
                    />
                  );
                })}
              />
            </div>
          )}

          {showWeeks && screenSize < windowBreakpoints.sm && (
            <div className='mx-auto grid max-w-screen-xl grid-cols-2 px-20 pt-8 xs:px-4 sm:px-4 md:px-4'>
              {sortedWeeksOnMobile.map((it: any, index: number) => {
                return (
                  <CourseWeekCard
                    key={'doing-course-card-' + index}
                    id={it._id}
                    isCurrent={it.isCurrentWeek}
                    title={it.title}
                    date={formatPeriod(it.startAt, it.endAt)}
                    onCardClick={() => {
                      setSelectedWeekId(it._id);
                    }}
                    onClick={() => {
                      openProgram(courseShow, it._id);
                    }}
                  />
                );
              })}
            </div>
          )}

          {showWeeks && showWeekContentSection && (
            <div className='mx-auto max-w-screen-xl px-4 py-8 md:px-20'>
              <section className='rounded-lg border-hairline border-dashed border-brand-color-primary-pure p-6'>
                <p className='text-sm font-semibold uppercase tracking-wide text-mono-color-darker'>
                  Conteúdo da semana
                </p>
                <h2 className='pt-2 text-3xl font-bold text-mono-color-darkest'>
                  {weekWithContent?.title}
                </h2>

                <div className='pt-6'>
                  {weekAreas.map((area: any, index: number) => {
                    return (
                      <div
                        key={
                          area?.id || `${area?.slug || area?.title}-${index}`
                        }
                        className='pb-4 last:pb-0'
                      >
                        <p className='text-base font-bold text-mono-color-darkest'>
                          {area.title}
                        </p>
                        {!!area.contents.length && (
                          <p className='pt-1 text-sm text-mono-color-dark'>
                            {area.contents.join(', ')}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          )}
        </div>
      )}

      {showAllCourses && (
        <RegularCoursesList
          showMessageAlert={userHasMatriculatedCourse}
          goBack={() => {
            hideAllCourses();
            setShowMatriculatedCourse(true);
          }}
        />
      )}
    </div>
  );
}
